import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { DatePicker } from "../components/ui/date-picker";
import { toast } from "sonner";
import { 
  Upload,
  FileText,
  Loader2,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Save,
  Users,
  Calendar,
  IndianRupee,
  Building2,
  Shield,
  Edit3,
  X,
  Sparkles,
  Heart,
  Car,
  User,
  FileCheck,
  Plus,
  UserPlus,
  ShieldAlert,
  Trash2,
  RefreshCw,
  Edit2,
  ArrowRight
} from "lucide-react";
import { authGet, authPost, authPut, authUpload, getAuthToken } from "../utils/auth";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MapPolicy() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Renewal mapping mode
  const [searchParams] = useSearchParams();
  const renewalOfId = searchParams.get("renewalOf");
  const [oldPolicy, setOldPolicy] = useState(null);

  // Platforms state
  const [platforms, setPlatforms] = useState([]);
  const [showAddPlatform, setShowAddPlatform] = useState(false);
  const [newPlatformName, setNewPlatformName] = useState("");
  const [addingPlatform, setAddingPlatform] = useState(false);
  const [showRenamePlatform, setShowRenamePlatform] = useState(false);
  const [renamePlatformName, setRenamePlatformName] = useState("");
  
  // Form data (editable after extraction) - Commission removed, handled in Revenue Dashboard
  const [formData, setFormData] = useState({
    // Common fields
    insurance_category: "Health", // Health, Life, or Vehicle
    proposer_name: "",
    proposer_mobile: "",
    proposer_email: "",
    policy_number: "",
    insurance_company: "",
    plan_name: "",
    policy_start_date: "",
    policy_end_date: "",
    payment_date: "", // Date when premium was actually paid - used for contest calculations
    policy_term_years: 1, // 1, 2, 3, 4, 5... up to 10 years
    premium_amount: "",
    payment_frequency: "Annual",
    policy_type: "Fresh", // Fresh, Portability, Renewal
    previous_insurer: "",
    previous_policy_number: "",
    notes: "",
    platform: "Direct",
    referred_by: "Direct",
    
    // Health Insurance specific
    sum_insured: "",
    family_members: [],
    insured_members_details: [],
    riders: [],
    room_rent_limit: "",
    copay_percentage: "",
    deductible: "",
    waiting_period_ped: "",
    zone: "",
    tpa_name: "",
    
    // Life Insurance specific
    sum_assured: "",
    life_plan_type: "", // Term, Endowment, ULIP, Whole Life, Money Back
    premium_payment_term: "",
    policy_term: "",
    life_assured_name: "",
    life_assured_age: "",
    life_assured_dob: "",
    nominee_name: "",
    nominee_relationship: "",
    maturity_benefit: "",
    
    // Vehicle Insurance specific
    vehicle_type: "", // Car, Two-Wheeler, Commercial
    vehicle_registration_number: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_variant: "",
    manufacturing_year: "",
    engine_number: "",
    chassis_number: "",
    fuel_type: "",
    cubic_capacity: "",
    idv: "",
    ncb_percentage: "",
    od_premium: "",
    tp_premium: "",
    addons: [],
    pa_cover: "",

    // Personal Accident (PA) specific
    accidental_death_benefit: "",
    permanent_total_disability: "",
    permanent_partial_disability: "",
    temporary_total_disability: "",
    medical_expenses: "",
    hospital_cash: "",
    education_benefit: "",
    loan_protection: "",
    worldwide_cover: "",
    exclusions: []
  });

  useEffect(() => {
    checkAuth();
    fetchPlatforms();
    if (renewalOfId) fetchOldPolicy(renewalOfId);
  }, []);

  const fetchOldPolicy = async (id) => {
    try {
      const res = await authGet(`/api/advisor/policies/${id}`);
      if (res.ok) setOldPolicy(res.data?.policy || null);
    } catch (err) {
      console.error("Failed to fetch original policy:", err);
    }
  };

  const checkAuth = async () => {
    const token = getAuthToken();
    if (!token) {
      navigate('/advisor/login');
      return;
    }
    try {
      const authRes = await authGet('/api/auth/me');
      if (!authRes.ok) {
        navigate('/advisor/login');
      }
    } catch {
      navigate('/advisor/login');
    }
  };

  const fetchPlatforms = async () => {
    try {
      const res = await authGet('/api/advisor/platforms');
      if (res.ok) {
        setPlatforms(res.data?.platforms || []);
      }
    } catch (err) {
      console.error("Failed to fetch platforms:", err);
    }
  };

  const handleAddPlatform = async () => {
    if (!newPlatformName.trim()) { toast.error("Enter platform name"); return; }
    setAddingPlatform(true);
    try {
      const res = await authPost('/api/advisor/platforms', { name: newPlatformName.trim() });
      if (!res.ok) throw new Error(res.data?.detail || "Failed to add");
      setPlatforms(prev => [...prev, newPlatformName.trim()]);
      setFormData(prev => ({ ...prev, platform: newPlatformName.trim() }));
      setNewPlatformName("");
      setShowAddPlatform(false);
      toast.success("Platform added");
    } catch (e) { toast.error(e.message); }
    setAddingPlatform(false);
  };

  const handleDeletePlatform = async (name) => {
    try {
      const token = getAuthToken();
      const delRes = await fetch(`${API_URL}/api/advisor/platforms/${encodeURIComponent(name)}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (delRes.ok) {
        setPlatforms(prev => prev.filter(p => p !== name));
        if (formData.platform === name) setFormData(prev => ({ ...prev, platform: platforms[0] || "Direct" }));
        toast.success(`"${name}" removed`);
      } else {
        const d = await delRes.json().catch(() => ({}));
        toast.error(d.detail || "Failed to delete");
      }
    } catch (e) { toast.error("Failed to delete platform"); }
  };

  const handleRenamePlatform = async () => {
    const oldName = formData.platform;
    const newName = renamePlatformName.trim();
    if (!newName) { toast.error("Enter new platform name"); return; }
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/api/advisor/platforms/${encodeURIComponent(oldName)}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_name: newName })
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setPlatforms(prev => prev.map(p => (p === oldName ? newName : p)));
        setFormData(prev => ({ ...prev, platform: newName }));
        setShowRenamePlatform(false);
        setRenamePlatformName("");
        toast.success(`Renamed to "${newName}"`);
      } else toast.error(d.detail || "Failed to rename");
    } catch (e) { toast.error("Failed to rename platform"); }
  };

  // Calculate policy term from dates
  const calculatePolicyTerm = (startDate, endDate) => {
    if (!startDate || !endDate) return 1;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMs = end - start;
    const diffYears = diffMs / (365.25 * 24 * 60 * 60 * 1000);
    return Math.max(1, Math.round(diffYears));
  };

  // Handle date change and auto-calculate policy term
  const handleDateChange = (field, value) => {
    setFormData(prev => {
      const newForm = { ...prev, [field]: value };
      // Auto-calculate policy term when both dates are set
      if (newForm.policy_start_date && newForm.policy_end_date) {
        newForm.policy_term_years = calculatePolicyTerm(newForm.policy_start_date, newForm.policy_end_date);
      }
      // Auto-set payment_date to policy_start_date when start date changes
      if (field === 'policy_start_date' && value) {
        newForm.payment_date = value;
      }
      return newForm;
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setExtractedData(null);
      setEditMode(false);
      // Auto-extract when file is selected
      handleExtract(file);
    } else {
      toast.error("Please select a PDF file");
    }
  };

  const handleExtract = async (file) => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setExtracting(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await authUpload('/api/advisor/extract-policy', formDataUpload);

      if (!response.ok) {
        throw new Error(response.data?.detail || "Failed to extract policy data");
      }

      setExtractedData(response.data.extracted_data);
      
      // Populate form with extracted data based on insurance category
      const extracted = response.data.extracted_data;
      const category = extracted.insurance_category || "Health";
      
      const newFormData = {
        // Common fields
        insurance_category: category,
        proposer_name: extracted.proposer_name || "",
        proposer_mobile: extracted.proposer_mobile || "",
        proposer_email: extracted.proposer_email || "",
        policy_number: extracted.policy_number || "",
        insurance_company: extracted.insurance_company || "",
        plan_name: extracted.plan_name || "",
        policy_start_date: extracted.policy_start_date || "",
        policy_end_date: extracted.policy_end_date || "",
        // Auto-default payment_date to policy_start_date (user can edit if needed)
        payment_date: extracted.policy_start_date || "",
        policy_term_years: calculatePolicyTerm(extracted.policy_start_date, extracted.policy_end_date),
        premium_amount: extracted.premium_amount || "",
        payment_frequency: extracted.payment_frequency || "Annual",
        policy_type: extracted.policy_type || "Fresh",
        previous_insurer: extracted.previous_insurer || "",
        previous_policy_number: extracted.previous_policy_number || "",
        notes: extracted.notes || "",
        platform: "Direct",
        referred_by: "Direct",
        policy_document_path: extracted.policy_document_path || null,
        
        // Health fields
        sum_insured: extracted.sum_insured || "",
        family_members: extracted.family_members || [],
        insured_members_details: extracted.insured_members_details || extracted.insured_members || [],
        riders: extracted.riders || [],
        room_rent_limit: extracted.room_rent_limit || "",
        copay_percentage: extracted.copay_percentage || "",
        deductible: extracted.deductible || "",
        waiting_period_ped: extracted.waiting_period_ped || "",
        zone: extracted.zone || "",
        tpa_name: extracted.tpa_name || "",
        
        // Life fields
        sum_assured: extracted.sum_assured || "",
        life_plan_type: extracted.life_plan_type || "",
        premium_payment_term: extracted.premium_payment_term || "",
        policy_term: extracted.policy_term || "",
        life_assured_name: extracted.life_assured_name || "",
        life_assured_age: extracted.life_assured_age || "",
        life_assured_dob: extracted.life_assured_dob || "",
        nominee_name: extracted.nominee_name || "",
        nominee_relationship: extracted.nominee_relationship || "",
        maturity_benefit: extracted.maturity_benefit || "",
        
        // Vehicle fields
        vehicle_type: extracted.vehicle_type || "",
        vehicle_registration_number: extracted.vehicle_registration_number || "",
        vehicle_make: extracted.vehicle_make || "",
        vehicle_model: extracted.vehicle_model || "",
        vehicle_variant: extracted.vehicle_variant || "",
        manufacturing_year: extracted.manufacturing_year || "",
        engine_number: extracted.engine_number || "",
        chassis_number: extracted.chassis_number || "",
        fuel_type: extracted.fuel_type || "",
        cubic_capacity: extracted.cubic_capacity || "",
        idv: extracted.idv || "",
        ncb_percentage: extracted.ncb_percentage || "",
        od_premium: extracted.od_premium || "",
        tp_premium: extracted.tp_premium || "",
        addons: extracted.addons || [],
        pa_cover: extracted.pa_cover || "",

        // Personal Accident (PA) fields
        accidental_death_benefit: extracted.accidental_death_benefit || "",
        permanent_total_disability: extracted.permanent_total_disability || "",
        permanent_partial_disability: extracted.permanent_partial_disability || "",
        temporary_total_disability: extracted.temporary_total_disability || "",
        medical_expenses: extracted.medical_expenses || "",
        hospital_cash: extracted.hospital_cash || "",
        education_benefit: extracted.education_benefit || "",
        loan_protection: extracted.loan_protection || "",
        worldwide_cover: extracted.worldwide_cover || "",
        exclusions: extracted.exclusions || []
      };
      
      setFormData(newFormData);

      if (oldPolicy) {
        const sameCompany = (extracted.insurance_company || "").trim().toLowerCase() === (oldPolicy.insurance_company || "").trim().toLowerCase();
        setFormData(prev => ({
          ...prev,
          proposer_name: prev.proposer_name || oldPolicy.proposer_name || "",
          policy_type: sameCompany ? "Renewal" : "Portability",
          previous_insurer: oldPolicy.insurance_company || "",
          previous_policy_number: oldPolicy.policy_number || "",
          platform: oldPolicy.platform || "Direct",
          referred_by: oldPolicy.referred_by || "Direct"
        }));
      }

      toast.success(`${category} Insurance policy extracted! Review and save.`);
      
    } catch (error) {
      console.error("Extraction error:", error);
      toast.error(error.message || "Failed to extract policy data");
    } finally {
      setExtracting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSavePolicy = async () => {
    // Validate required fields
    if (!formData.proposer_name?.trim()) {
      toast.error("Proposer name is required");
      return;
    }
    if (!formData.policy_number || !formData.insurance_company) {
      toast.error("Policy number and insurance company are required");
      return;
    }

    setSaving(true);

    try {
      let clientId;
      
      // Renewal mapping - reuse the original policy's client, skip lookup/creation
      const clientsRes = !oldPolicy
        ? await authGet(`/api/advisor/clients?search=${encodeURIComponent(formData.proposer_name.trim())}`)
        : null;
      
      if (oldPolicy) {
        clientId = oldPolicy.client_id;
      }
      
      // Check if client with exact name exists
      const existingClient = clientsRes?.data?.clients?.find(
        c => c.name.toLowerCase() === formData.proposer_name.trim().toLowerCase()
      );
      
      if (!oldPolicy && existingClient) {
        clientId = existingClient.id;
        toast.info(`Found existing client: ${existingClient.name}`);
        
        // Update client mobile/email if we have new values
        if ((formData.proposer_mobile || formData.proposer_email) && 
            (!existingClient.mobile || !existingClient.email)) {
          await authPut(`/api/advisor/clients/${existingClient.id}`, {
            mobile: formData.proposer_mobile || existingClient.mobile || "",
            email: formData.proposer_email || existingClient.email || ""
          });
        }
      } else if (!oldPolicy) {
        // Create new client automatically with mobile and email
        const clientRes = await authPost('/api/advisor/clients', {
          name: formData.proposer_name.trim(),
          mobile: formData.proposer_mobile || "",
          email: formData.proposer_email || ""
        });
        
        if (!clientRes.ok) {
          throw new Error(clientRes.data?.detail || "Failed to create client");
        }

        clientId = clientRes.data.client_id;
        toast.success(`New client created: ${formData.proposer_name}`);
      }

      // Step 2: Create the policy with category-specific fields
      const policyPayload = {
        client_id: clientId,
        // Common fields
        insurance_category: formData.insurance_category || "Health",
        insurance_company: formData.insurance_company,
        plan_name: formData.plan_name || formData.insurance_company,
        policy_number: formData.policy_number,
        premium_amount: formData.premium_amount || "0",
        policy_start_date: formData.policy_start_date || new Date().toISOString().split('T')[0],
        policy_end_date: formData.policy_end_date || "",
        payment_date: formData.payment_date || null, // Date premium was paid - for contest calculations
        policy_term_years: formData.policy_term_years || 1,
        payment_frequency: formData.payment_frequency || "Annual",
        platform: formData.platform || "Direct",
        notes: formData.notes || "",
        referred_by: formData.referred_by || "Direct",
        proposer_name: formData.proposer_name,
        policy_issue_month: formData.policy_start_date ? formData.policy_start_date.slice(0, 7) : null,
        policy_type: formData.policy_type || "Fresh",
        previous_insurer: formData.previous_insurer || null,
        previous_policy_number: formData.previous_policy_number || null,
        policy_document_path: formData.policy_document_path || null
      };
      
      // Add category-specific fields
      if (formData.insurance_category === "Health") {
        Object.assign(policyPayload, {
          sum_insured: formData.sum_insured || "Not specified",
          family_members: formData.family_members || [],
          insured_members_details: formData.insured_members_details || [],
          riders: formData.riders || [],
          room_rent_limit: formData.room_rent_limit || null,
          copay_percentage: formData.copay_percentage || null,
          deductible: formData.deductible || null,
          waiting_period_ped: formData.waiting_period_ped || null,
          zone: formData.zone || null,
          tpa_name: formData.tpa_name || null
        });
      } else if (formData.insurance_category === "Life") {
        Object.assign(policyPayload, {
          sum_assured: formData.sum_assured || "Not specified",
          life_plan_type: formData.life_plan_type || null,
          premium_payment_term: formData.premium_payment_term || null,
          policy_term: formData.policy_term || null,
          life_assured_name: formData.life_assured_name || null,
          life_assured_age: formData.life_assured_age || null,
          life_assured_dob: formData.life_assured_dob || null,
          nominee_name: formData.nominee_name || null,
          nominee_relationship: formData.nominee_relationship || null,
          maturity_benefit: formData.maturity_benefit || null,
          riders: formData.riders || []
        });
      } else if (formData.insurance_category === "Vehicle") {
        Object.assign(policyPayload, {
          vehicle_type: formData.vehicle_type || null,
          vehicle_registration_number: formData.vehicle_registration_number || null,
          vehicle_make: formData.vehicle_make || null,
          vehicle_model: formData.vehicle_model || null,
          vehicle_variant: formData.vehicle_variant || null,
          manufacturing_year: formData.manufacturing_year || null,
          engine_number: formData.engine_number || null,
          chassis_number: formData.chassis_number || null,
          fuel_type: formData.fuel_type || null,
          cubic_capacity: formData.cubic_capacity || null,
          idv: formData.idv || null,
          ncb_percentage: formData.ncb_percentage || null,
          od_premium: formData.od_premium || null,
          tp_premium: formData.tp_premium || null,
          addons: formData.addons || [],
          pa_cover: formData.pa_cover || null
        });
      } else if (formData.insurance_category === "PA") {
        Object.assign(policyPayload, {
          sum_insured: formData.sum_insured || "Not specified",
          insured_members_details: formData.insured_members_details || [],
          riders: formData.riders || [],
          accidental_death_benefit: formData.accidental_death_benefit || null,
          permanent_total_disability: formData.permanent_total_disability || null,
          permanent_partial_disability: formData.permanent_partial_disability || null,
          temporary_total_disability: formData.temporary_total_disability || null,
          medical_expenses: formData.medical_expenses || null,
          hospital_cash: formData.hospital_cash || null,
          education_benefit: formData.education_benefit || null,
          loan_protection: formData.loan_protection || null,
          worldwide_cover: formData.worldwide_cover || null,
          exclusions: formData.exclusions || []
        });
      }

      const policyRes = oldPolicy
        ? await authPost(`/api/advisor/policies/${oldPolicy.id}/renew`, policyPayload)
        : await authPost('/api/advisor/policies', policyPayload);
      
      if (!policyRes.ok) {
        // Check for duplicate policy error (409 Conflict)
        if (policyRes.status === 409) {
          const errorDetail = policyRes.data?.detail || "Duplicate policy detected! This policy already exists in the system.";
          toast.error(errorDetail, { duration: 8000 });
          return;
        }
        throw new Error(policyRes.data?.detail || "Failed to save policy");
      }

      if (oldPolicy) {
        toast.success(`Renewed policy mapped! ${policyRes.data?.renewal_change || ""}`, { duration: 6000 });
        navigate(`/advisor/clients/${oldPolicy.client_id}`);
        return;
      }

      toast.success("Policy saved successfully!");
      
      // Reset for next upload
      setSelectedFile(null);
      setExtractedData(null);
      setEditMode(false);
      setFormData({
        proposer_name: "",
        proposer_mobile: "",
        proposer_email: "",
        policy_number: "",
        insurance_company: "",
        plan_name: "",
        policy_start_date: "",
        policy_end_date: "",
        payment_date: "",
        premium_amount: "",
        payment_frequency: "Annual",
        sum_insured: "",
        family_members: [],
        insured_members_details: [],
        riders: [],
        notes: "",
        platform: "Direct",
        // Referral source
        referred_by: "Direct",
        // Fresh vs Portability
        policy_type: "Fresh",
        previous_insurer: "",
        previous_policy_number: ""
      });

    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.message || "Failed to save policy");
    } finally {
      setSaving(false);
    }
  };

  const norm = (s) => (s || "").trim().toLowerCase();
  let renewalComparison = null;
  if (oldPolicy && extractedData) {
    const sameCompany = norm(extractedData.insurance_company) === norm(oldPolicy.insurance_company);
    const samePlan = norm(extractedData.plan_name) === norm(oldPolicy.plan_name);
    const sameNumber = norm(extractedData.policy_number) === norm(oldPolicy.policy_number);
    renewalComparison = {
      sameCompany, samePlan, sameNumber,
      label: sameCompany && (samePlan || sameNumber) ? "Same Policy Renewed" : sameCompany ? "Plan Changed (Same Company)" : "Company Changed (Ported)",
      tone: sameCompany && (samePlan || sameNumber) ? "green" : sameCompany ? "amber" : "purple"
    };
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/advisor/dashboard')}
            className="text-[#3A4557] hover:text-[#1B2A4A]"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#1B2A4A]">{oldPolicy ? "Map Renewed Policy" : "Map New Policy"}</h1>
            <p className="text-[#3A4557] text-sm">{oldPolicy ? "Upload the renewed policy PDF - AI extracts & links it to the old policy" : "Upload policy PDF - AI extracts everything automatically"}</p>
          </div>
        </div>

        {/* Renewal Mode Banner */}
        {oldPolicy && (
          <Card className="bg-[#D4A017]/10 border-[#D4A017]/40 mb-6" data-testid="renewal-old-policy-banner">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <RefreshCw className="h-5 w-5 text-[#A87608]" />
                <span className="text-[#A87608] font-semibold">Renewing This Policy</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-[#3A4557] text-xs">Client / Proposer</p>
                  <p className="text-[#1B2A4A] font-medium">{oldPolicy.proposer_name || "N/A"}</p>
                </div>
                <div>
                  <p className="text-[#3A4557] text-xs">Company</p>
                  <p className="text-[#1B2A4A] font-medium">{oldPolicy.insurance_company}</p>
                </div>
                <div>
                  <p className="text-[#3A4557] text-xs">Plan</p>
                  <p className="text-[#1B2A4A] font-medium">{oldPolicy.plan_name}</p>
                </div>
                <div>
                  <p className="text-[#3A4557] text-xs">Policy No. / Expiry</p>
                  <p className="text-[#1B2A4A] font-medium">{oldPolicy.policy_number}</p>
                  <p className="text-red-600 text-xs">{oldPolicy.policy_end_date}</p>
                </div>
              </div>
              <p className="text-[#3A4557] text-xs mt-3">Old policy details will be preserved in the system and marked as Renewed after mapping.</p>
            </CardContent>
          </Card>
        )}

        {/* Upload Area */}
        <Card className="bg-white/50 border-[#C9D4E6] mb-6">
          <CardContent className="p-6">
            <div 
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                extracting 
                  ? 'border-[#D4A017]/50 bg-[#D4A017]/5' 
                  : selectedFile 
                    ? 'border-green-500/50 bg-green-500/5' 
                    : 'border-[#C9D4E6] hover:border-[#D4A017]/50'
              }`}
              onClick={() => !extracting && fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                disabled={extracting}
              />
              
              {extracting ? (
                <div className="py-4">
                  <Loader2 className="h-12 w-12 text-[#A87608] animate-spin mx-auto mb-4" />
                  <p className="text-[#1B2A4A] text-lg font-medium">Extracting policy data...</p>
                  <p className="text-[#3A4557] text-sm mt-2">AI is reading your document</p>
                </div>
              ) : selectedFile && extractedData ? (
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="h-10 w-10 text-green-500" />
                  <div className="text-left">
                    <p className="text-[#1B2A4A] font-medium">{selectedFile.name}</p>
                    <p className="text-green-600 text-sm">Data extracted successfully!</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setExtractedData(null);
                    }}
                    className="text-[#3A4557] hover:text-red-600 ml-4"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-[#D4A017]/20 to-[#D9A82C]/20 flex items-center justify-center mx-auto mb-4">
                    <Upload className="h-8 w-8 text-[#A87608]" />
                  </div>
                  <p className="text-[#1B2A4A] text-lg font-medium">Drop policy PDF here</p>
                  <p className="text-[#3A4557] text-sm mt-1">or click to browse</p>
                  <div className="flex items-center justify-center gap-2 mt-4 text-[#A87608] text-sm">
                    <Sparkles className="h-4 w-4" />
                    AI will extract all details automatically
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Renewal Comparison */}
        {oldPolicy && extractedData && renewalComparison && (
          <Card className={`mb-6 ${
            renewalComparison.tone === "green" ? "bg-emerald-500/10 border-emerald-500/40" :
            renewalComparison.tone === "amber" ? "bg-[#D4A017]/10 border-[#D4A017]/40" :
            "bg-purple-500/10 border-purple-500/40"
          }`} data-testid="renewal-comparison-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                {renewalComparison.tone === "green" ? (
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                ) : (
                  <AlertCircle className={`h-5 w-5 ${renewalComparison.tone === "amber" ? "text-[#A87608]" : "text-purple-600"}`} />
                )}
                <span className={`font-semibold ${
                  renewalComparison.tone === "green" ? "text-emerald-600" :
                  renewalComparison.tone === "amber" ? "text-[#A87608]" : "text-purple-600"
                }`} data-testid="renewal-change-label">{renewalComparison.label}</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[#3A4557] w-24 flex-shrink-0">Company:</span>
                  <span className="text-[#1B2A4A]">{oldPolicy.insurance_company}</span>
                  <ArrowRight className="h-3 w-3 text-[#3A4557] flex-shrink-0" />
                  <span className={renewalComparison.sameCompany ? "text-emerald-600" : "text-purple-600 font-medium"}>{extractedData.insurance_company || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[#3A4557] w-24 flex-shrink-0">Plan:</span>
                  <span className="text-[#1B2A4A]">{oldPolicy.plan_name}</span>
                  <ArrowRight className="h-3 w-3 text-[#3A4557] flex-shrink-0" />
                  <span className={renewalComparison.samePlan ? "text-emerald-600" : "text-[#A87608] font-medium"}>{extractedData.plan_name || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[#3A4557] w-24 flex-shrink-0">Policy No.:</span>
                  <span className="text-[#1B2A4A]">{oldPolicy.policy_number}</span>
                  <ArrowRight className="h-3 w-3 text-[#3A4557] flex-shrink-0" />
                  <span className={renewalComparison.sameNumber ? "text-emerald-600" : "text-[#A87608] font-medium"}>{extractedData.policy_number || "N/A"}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Extracted Data Display */}
        {extractedData && (
          <Card className="bg-white/50 border-[#C9D4E6] mb-6">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-[#A87608] flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Extracted Policy Details
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditMode(!editMode)}
                className="border-[#C9D4E6] text-[#2E3B57]"
              >
                <Edit3 className="h-4 w-4 mr-2" />
                {editMode ? 'Done Editing' : 'Edit'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Insurance Category Selector */}
              <div className="p-4 bg-gradient-to-r from-[#EDF1F8]/50 to-[#EDF1F8]/50 rounded-lg border border-[#C9D4E6]">
                <div className="flex items-center gap-2 mb-3">
                  <FileCheck className="h-5 w-5 text-[#A87608]" />
                  <span className="text-[#A87608] font-medium">Insurance Category</span>
                  <span className="text-xs text-[#3A4557] ml-2">(Auto-detected)</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { id: 'Health', icon: Heart, color: 'emerald', label: 'Health' },
                    { id: 'Life', icon: User, color: 'blue', label: 'Life' },
                    { id: 'PA', icon: ShieldAlert, color: 'purple', label: 'Personal Accident' },
                    { id: 'Vehicle', icon: Car, color: 'amber', label: 'Vehicle' }
                  ].map(cat => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => handleInputChange('insurance_category', cat.id)}
                      className={`p-3 rounded-lg border flex items-center gap-2 transition-all ${
                        formData.insurance_category === cat.id
                          ? `bg-${cat.color}-500/20 border-${cat.color}-500/50 text-${cat.color}-700`
                          : 'bg-[#EDF1F8]/50 border-[#C9D4E6] text-[#3A4557] hover:border-[#C9D4E6]'
                      }`}
                      style={formData.insurance_category === cat.id ? {
                        backgroundColor: cat.color === 'emerald' ? 'rgba(16, 185, 129, 0.2)' :
                                        cat.color === 'blue' ? 'rgba(59, 130, 246, 0.2)' :
                                        cat.color === 'purple' ? 'rgba(168, 85, 247, 0.2)' :
                                        'rgba(245, 158, 11, 0.2)',
                        borderColor: cat.color === 'emerald' ? 'rgba(16, 185, 129, 0.5)' :
                                    cat.color === 'blue' ? 'rgba(59, 130, 246, 0.5)' :
                                    cat.color === 'purple' ? 'rgba(168, 85, 247, 0.5)' :
                                    'rgba(245, 158, 11, 0.5)',
                        color: cat.color === 'emerald' ? '#34d399' :
                              cat.color === 'blue' ? '#60a5fa' :
                              cat.color === 'purple' ? '#c084fc' :
                              '#fbbf24'
                      } : {}}
                    >
                      <cat.icon className="h-5 w-5" />
                      <span className="text-sm font-medium">{cat.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Client Info */}
              <div className="p-4 bg-[#EDF1F8]/30 border border-blue-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-5 w-5 text-[#2E3B57]" />
                  <span className="text-[#2E3B57] font-medium">Client (Proposer)</span>
                </div>
                {editMode ? (
                  <div className="space-y-3">
                    <Input
                      value={formData.proposer_name}
                      onChange={(e) => handleInputChange('proposer_name', e.target.value)}
                      className="bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-lg"
                      placeholder="Proposer Name"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        value={formData.proposer_mobile}
                        onChange={(e) => handleInputChange('proposer_mobile', e.target.value)}
                        className="bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                        placeholder="Mobile Number"
                      />
                      <Input
                        value={formData.proposer_email}
                        onChange={(e) => handleInputChange('proposer_email', e.target.value)}
                        className="bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                        placeholder="Email Address"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-[#1B2A4A] text-xl font-semibold">{formData.proposer_name || 'Not found'}</p>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="text-[#3A4557]">
                        📱 {formData.proposer_mobile || <span className="text-[#A87608] italic">Fill manually</span>}
                      </span>
                      <span className="text-[#3A4557]">
                        ✉️ {formData.proposer_email || <span className="text-[#A87608] italic">Fill manually</span>}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Policy Type - Fresh / Portability / Renewal */}
              <div className={`p-4 rounded-lg border ${
                formData.policy_type === 'Portability' ? 'bg-purple-500/10 border-purple-500/30' : 
                formData.policy_type === 'Renewal' ? 'bg-[#EDF1F8]/30 border-blue-500/30' : 
                'bg-green-500/10 border-green-500/30'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                      formData.policy_type === 'Portability' ? 'bg-purple-500/20' : 
                      formData.policy_type === 'Renewal' ? 'bg-[#EDF1F8]/40' : 
                      'bg-green-500/20'
                    }`}>
                      {formData.policy_type === 'Portability' ? (
                        <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                      ) : formData.policy_type === 'Renewal' ? (
                        <svg className="h-5 w-5 text-[#2E3B57]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                      ) : (
                        <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      )}
                    </div>
                    <div>
                      <p className={`font-bold text-lg ${
                        formData.policy_type === 'Portability' ? 'text-purple-600' : 
                        formData.policy_type === 'Renewal' ? 'text-[#2E3B57]' : 
                        'text-green-600'
                      }`}>
                        {formData.policy_type === 'Portability' ? 'PORTABILITY CASE' : 
                         formData.policy_type === 'Renewal' ? 'RENEWAL CASE' : 
                         'FRESH CASE'}
                      </p>
                      {formData.policy_type === 'Portability' && formData.previous_insurer && (
                        <p className="text-[#3A4557] text-sm">From: {formData.previous_insurer}</p>
                      )}
                      {formData.policy_type === 'Renewal' && formData.previous_policy_number && (
                        <p className="text-[#3A4557] text-sm">Previous: {formData.previous_policy_number}</p>
                      )}
                    </div>
                  </div>
                  {editMode && (
                    <select
                      value={formData.policy_type}
                      onChange={(e) => handleInputChange('policy_type', e.target.value)}
                      className="h-10 px-3 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A]"
                      data-testid="policy-type-select"
                    >
                      <option value="Fresh">Fresh (New Policy)</option>
                      <option value="Portability">Portability (From Other Insurer)</option>
                      <option value="Renewal">Renewal (Same Insurer)</option>
                    </select>
                  )}
                </div>
                {(formData.policy_type === 'Portability' || formData.policy_type === 'Renewal') && editMode && (
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    {formData.policy_type === 'Portability' && (
                      <div>
                        <Label className="text-[#3A4557] text-xs">Previous Insurer</Label>
                        <Input
                          value={formData.previous_insurer}
                          onChange={(e) => handleInputChange('previous_insurer', e.target.value)}
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                          placeholder="Previous company name"
                        />
                      </div>
                    )}
                    <div className={formData.policy_type === 'Renewal' ? 'col-span-2' : ''}>
                      <Label className="text-[#3A4557] text-xs">Previous Policy No.</Label>
                      <Input
                        value={formData.previous_policy_number}
                        onChange={(e) => handleInputChange('previous_policy_number', e.target.value)}
                        className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                        placeholder="Previous policy number"
                      />
                    </div>
                  </div>
                )}
                {(formData.policy_type === 'Portability' || formData.policy_type === 'Renewal') && !editMode && formData.previous_policy_number && (
                  <p className="text-[#3A4557] text-sm mt-2">Previous Policy: {formData.previous_policy_number}</p>
                )}
              </div>

              {/* Policy Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#3A4557] text-sm">Insurance Company</Label>
                  {editMode ? (
                    <Input
                      value={formData.insurance_company}
                      onChange={(e) => handleInputChange('insurance_company', e.target.value)}
                      className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                    />
                  ) : (
                    <p className="text-[#1B2A4A] font-medium mt-1">{formData.insurance_company || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-[#3A4557] text-sm">Plan Name</Label>
                  {editMode ? (
                    <Input
                      value={formData.plan_name}
                      onChange={(e) => handleInputChange('plan_name', e.target.value)}
                      className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                    />
                  ) : (
                    <p className="text-[#1B2A4A] font-medium mt-1">{formData.plan_name || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-[#3A4557] text-sm">Policy Number</Label>
                  {editMode ? (
                    <Input
                      value={formData.policy_number}
                      onChange={(e) => handleInputChange('policy_number', e.target.value)}
                      className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                    />
                  ) : (
                    <p className="text-[#1B2A4A] font-medium mt-1">{formData.policy_number || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-[#3A4557] text-sm">
                    {formData.insurance_category === 'Life' ? 'Sum Assured' : 
                     formData.insurance_category === 'Vehicle' ? 'IDV (Insured Declared Value)' : 
                     formData.insurance_category === 'PA' ? 'Capital Sum Insured' :
                     'Sum Insured'}
                  </Label>
                  {editMode ? (
                    <Input
                      value={formData.insurance_category === 'Life' ? formData.sum_assured : 
                             formData.insurance_category === 'Vehicle' ? formData.idv : 
                             formData.sum_insured}
                      onChange={(e) => handleInputChange(
                        formData.insurance_category === 'Life' ? 'sum_assured' : 
                        formData.insurance_category === 'Vehicle' ? 'idv' : 
                        'sum_insured', 
                        e.target.value
                      )}
                      className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                    />
                  ) : (
                    <p className="text-[#1B2A4A] font-medium mt-1">
                      {formData.insurance_category === 'Life' ? formData.sum_assured : 
                       formData.insurance_category === 'Vehicle' ? (formData.idv ? `₹${formData.idv}` : '-') : 
                       formData.sum_insured || '-'}
                    </p>
                  )}
                </div>
              </div>

              {/* Dates & Premium */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label className="text-[#3A4557] text-sm mb-1 block">Start Date</Label>
                  {editMode ? (
                    <DatePicker
                      date={formData.policy_start_date}
                      onDateChange={(date) => handleDateChange('policy_start_date', date)}
                      placeholder="Select start date"
                    />
                  ) : (
                    <p className="text-[#1B2A4A] font-medium mt-1">{formData.policy_start_date || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-[#3A4557] text-sm mb-1 block">Renewal Date</Label>
                  {editMode ? (
                    <DatePicker
                      date={formData.policy_end_date}
                      onDateChange={(date) => handleDateChange('policy_end_date', date)}
                      placeholder="Select renewal date"
                    />
                  ) : (
                    <p className="text-[#1B2A4A] font-medium mt-1">{formData.policy_end_date || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-[#3A4557] text-sm flex items-center gap-1 mb-1">
                    Payment Date
                    <span className="text-xs text-[#A87608]">(Contest)</span>
                  </Label>
                  {editMode ? (
                    <DatePicker
                      date={formData.payment_date}
                      onDateChange={(date) => handleInputChange('payment_date', date)}
                      placeholder="Select payment date"
                    />
                  ) : (
                    <p className="text-[#1B2A4A] font-medium mt-1">{formData.payment_date || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-[#3A4557] text-sm">Policy Term</Label>
                  {editMode ? (
                    <select
                      value={formData.policy_term_years}
                      onChange={(e) => handleInputChange('policy_term_years', parseInt(e.target.value))}
                      className="mt-1 w-full h-10 px-3 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A]"
                    >
                      <option value="1">1 Year</option>
                      <option value="2">2 Years</option>
                      <option value="3">3 Years</option>
                      <option value="4">4 Years</option>
                      <option value="5">5 Years</option>
                      <option value="6">6 Years</option>
                      <option value="7">7 Years</option>
                      <option value="8">8 Years</option>
                      <option value="9">9 Years</option>
                      <option value="10">10 Years</option>
                    </select>
                  ) : (
                    <p className="text-[#1B2A4A] font-medium mt-1">{formData.policy_term_years} Year{formData.policy_term_years > 1 ? 's' : ''}</p>
                  )}
                  {formData.policy_term_years > 1 && (
                    <p className="text-xs text-[#A87608] mt-1">
                      Commissionable: ₹{((parseFloat(formData.premium_amount) || 0) / formData.policy_term_years).toLocaleString('en-IN', { maximumFractionDigits: 0 })}/year
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-[#3A4557] text-sm">Premium (₹)</Label>
                  {editMode ? (
                    <Input
                      value={formData.premium_amount}
                      onChange={(e) => handleInputChange('premium_amount', e.target.value)}
                      className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                    />
                  ) : (
                    <p className="text-green-600 font-bold text-lg mt-1">₹{formData.premium_amount || '0'}</p>
                  )}
                </div>
              </div>

              {/* Payment Frequency */}
              <div>
                <Label className="text-[#3A4557] text-sm">Payment Frequency</Label>
                {editMode ? (
                  <select
                    value={formData.payment_frequency}
                    onChange={(e) => handleInputChange('payment_frequency', e.target.value)}
                    className="mt-1 w-full h-10 px-3 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A]"
                  >
                    <option value="Annual">Annual</option>
                    <option value="Half-Yearly">Half-Yearly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Single Premium">Single Premium</option>
                  </select>
                ) : (
                  <p className="text-[#1B2A4A] font-medium mt-1">{formData.payment_frequency}</p>
                )}
              </div>

              {/* Insured Members */}
              {formData.insured_members_details?.length > 0 && (
                <div>
                  <Label className="text-[#3A4557] text-sm flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4" />
                    Insured Members ({formData.insured_members_details.length})
                  </Label>
                  <div className="space-y-2">
                    {formData.insured_members_details.map((member, idx) => (
                      <div key={idx} className="p-3 bg-white/70 rounded-lg border border-[#C9D4E6] flex items-center justify-between">
                        <span className="text-[#1B2A4A]">{member.name}</span>
                        <span className="text-[#3A4557] text-sm">
                          {member.relationship} {member.age ? `• ${member.age} yrs` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Riders */}
              {formData.riders?.length > 0 && (
                <div>
                  <Label className="text-[#3A4557] text-sm flex items-center gap-2 mb-2">
                    <Shield className="h-4 w-4" />
                    Riders / Add-ons
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {formData.riders.map((rider, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-500/20 text-purple-700 rounded-full text-sm">
                        {rider}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Health Insurance Specific Fields */}
              {formData.insurance_category === 'Health' && (
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Heart className="h-5 w-5 text-emerald-600" />
                    <span className="text-emerald-600 font-medium">Health Insurance Details</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#3A4557] text-xs">Room Rent Limit</Label>
                      {editMode ? (
                        <Input
                          value={formData.room_rent_limit || ''}
                          onChange={(e) => handleInputChange('room_rent_limit', e.target.value)}
                          placeholder="e.g., No Limit, Single Private AC"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.room_rent_limit || 'Not specified'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Co-pay %</Label>
                      {editMode ? (
                        <Input
                          value={formData.copay_percentage || ''}
                          onChange={(e) => handleInputChange('copay_percentage', e.target.value)}
                          placeholder="e.g., None, 10%, 20%"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.copay_percentage || 'None'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">PED Waiting Period</Label>
                      {editMode ? (
                        <Input
                          value={formData.waiting_period_ped || ''}
                          onChange={(e) => handleInputChange('waiting_period_ped', e.target.value)}
                          placeholder="e.g., 3 years, 4 years"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.waiting_period_ped || 'Not specified'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Zone/City Tier</Label>
                      {editMode ? (
                        <Input
                          value={formData.zone || ''}
                          onChange={(e) => handleInputChange('zone', e.target.value)}
                          placeholder="e.g., Zone A, Metro"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.zone || 'Not specified'}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Life Insurance Specific Fields */}
              {formData.insurance_category === 'Life' && (
                <div className="p-4 bg-[#EDF1F8]/30 border border-blue-500/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="h-5 w-5 text-[#2E3B57]" />
                    <span className="text-[#2E3B57] font-medium">Life Insurance Details</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#3A4557] text-xs">Plan Type</Label>
                      {editMode ? (
                        <select
                          value={formData.life_plan_type || ''}
                          onChange={(e) => handleInputChange('life_plan_type', e.target.value)}
                          className="mt-1 w-full h-9 px-3 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm"
                        >
                          <option value="">Select Type</option>
                          <option value="Term">Term Insurance</option>
                          <option value="Endowment">Endowment</option>
                          <option value="ULIP">ULIP</option>
                          <option value="Whole Life">Whole Life</option>
                          <option value="Money Back">Money Back</option>
                        </select>
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.life_plan_type || 'Not specified'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Policy Term (Years)</Label>
                      {editMode ? (
                        <Input
                          value={formData.policy_term || ''}
                          onChange={(e) => handleInputChange('policy_term', e.target.value)}
                          placeholder="e.g., 20, 30"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.policy_term ? `${formData.policy_term} years` : 'Not specified'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Premium Payment Term</Label>
                      {editMode ? (
                        <Input
                          value={formData.premium_payment_term || ''}
                          onChange={(e) => handleInputChange('premium_payment_term', e.target.value)}
                          placeholder="e.g., 10, 15, Regular Pay"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.premium_payment_term || 'Not specified'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Life Assured</Label>
                      {editMode ? (
                        <Input
                          value={formData.life_assured_name || ''}
                          onChange={(e) => handleInputChange('life_assured_name', e.target.value)}
                          placeholder="Name of life assured"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.life_assured_name || formData.proposer_name || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Nominee Name</Label>
                      {editMode ? (
                        <Input
                          value={formData.nominee_name || ''}
                          onChange={(e) => handleInputChange('nominee_name', e.target.value)}
                          placeholder="Nominee name"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.nominee_name || 'Not specified'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Nominee Relationship</Label>
                      {editMode ? (
                        <Input
                          value={formData.nominee_relationship || ''}
                          onChange={(e) => handleInputChange('nominee_relationship', e.target.value)}
                          placeholder="e.g., Spouse, Son, Daughter"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.nominee_relationship || 'Not specified'}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Personal Accident Insurance Specific Fields */}
              {formData.insurance_category === 'PA' && (
                <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldAlert className="h-5 w-5 text-purple-600" />
                    <span className="text-purple-600 font-medium">Personal Accident Details</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#3A4557] text-xs">Capital Sum Insured</Label>
                      {editMode ? (
                        <Input
                          value={formData.sum_insured || ''}
                          onChange={(e) => handleInputChange('sum_insured', e.target.value)}
                          placeholder="e.g., 10 Lakhs, 50 Lakhs"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.sum_insured || 'Not specified'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Accidental Death Benefit</Label>
                      {editMode ? (
                        <Input
                          value={formData.accidental_death_benefit || ''}
                          onChange={(e) => handleInputChange('accidental_death_benefit', e.target.value)}
                          placeholder="100% of CSI"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.accidental_death_benefit || 'Not specified'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Permanent Total Disability (PTD)</Label>
                      {editMode ? (
                        <Input
                          value={formData.permanent_total_disability || ''}
                          onChange={(e) => handleInputChange('permanent_total_disability', e.target.value)}
                          placeholder="e.g., 100% of CSI"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.permanent_total_disability || 'Not specified'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Permanent Partial Disability (PPD)</Label>
                      {editMode ? (
                        <Input
                          value={formData.permanent_partial_disability || ''}
                          onChange={(e) => handleInputChange('permanent_partial_disability', e.target.value)}
                          placeholder="e.g., As per schedule"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.permanent_partial_disability || 'Not specified'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Medical Expenses Reimbursement</Label>
                      {editMode ? (
                        <Input
                          value={formData.medical_expenses || ''}
                          onChange={(e) => handleInputChange('medical_expenses', e.target.value)}
                          placeholder="e.g., 10% of CSI, Rs. 50,000"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.medical_expenses || 'Not specified'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Hospital Cash Benefit</Label>
                      {editMode ? (
                        <Input
                          value={formData.hospital_cash || ''}
                          onChange={(e) => handleInputChange('hospital_cash', e.target.value)}
                          placeholder="e.g., Rs. 1000/day"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.hospital_cash || 'Not specified'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Education Benefit</Label>
                      {editMode ? (
                        <Input
                          value={formData.education_benefit || ''}
                          onChange={(e) => handleInputChange('education_benefit', e.target.value)}
                          placeholder="e.g., Rs. 50,000 per child"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.education_benefit || 'Not specified'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Worldwide Cover</Label>
                      {editMode ? (
                        <select
                          value={formData.worldwide_cover || ''}
                          onChange={(e) => handleInputChange('worldwide_cover', e.target.value)}
                          className="mt-1 w-full h-9 px-3 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm"
                        >
                          <option value="">Select</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.worldwide_cover || 'Not specified'}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Vehicle Insurance Specific Fields */}
              {formData.insurance_category === 'Vehicle' && (
                <div className="p-4 bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Car className="h-5 w-5 text-[#A87608]" />
                    <span className="text-[#A87608] font-medium">Vehicle Details</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#3A4557] text-xs">Vehicle Type</Label>
                      {editMode ? (
                        <select
                          value={formData.vehicle_type || ''}
                          onChange={(e) => handleInputChange('vehicle_type', e.target.value)}
                          className="mt-1 w-full h-9 px-3 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm"
                        >
                          <option value="">Select Type</option>
                          <option value="Car">Car</option>
                          <option value="Two-Wheeler">Two-Wheeler</option>
                          <option value="Commercial">Commercial</option>
                        </select>
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.vehicle_type || 'Not specified'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Registration Number</Label>
                      {editMode ? (
                        <Input
                          value={formData.vehicle_registration_number || ''}
                          onChange={(e) => handleInputChange('vehicle_registration_number', e.target.value.toUpperCase())}
                          placeholder="e.g., MH01AB1234"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm uppercase"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1 font-mono">{formData.vehicle_registration_number || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Make</Label>
                      {editMode ? (
                        <Input
                          value={formData.vehicle_make || ''}
                          onChange={(e) => handleInputChange('vehicle_make', e.target.value)}
                          placeholder="e.g., Maruti, Honda"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.vehicle_make || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Model</Label>
                      {editMode ? (
                        <Input
                          value={formData.vehicle_model || ''}
                          onChange={(e) => handleInputChange('vehicle_model', e.target.value)}
                          placeholder="e.g., Swift, City"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.vehicle_model || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Manufacturing Year</Label>
                      {editMode ? (
                        <Input
                          value={formData.manufacturing_year || ''}
                          onChange={(e) => handleInputChange('manufacturing_year', e.target.value)}
                          placeholder="e.g., 2022"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.manufacturing_year || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Fuel Type</Label>
                      {editMode ? (
                        <select
                          value={formData.fuel_type || ''}
                          onChange={(e) => handleInputChange('fuel_type', e.target.value)}
                          className="mt-1 w-full h-9 px-3 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm"
                        >
                          <option value="">Select</option>
                          <option value="Petrol">Petrol</option>
                          <option value="Diesel">Diesel</option>
                          <option value="CNG">CNG</option>
                          <option value="Electric">Electric</option>
                          <option value="Hybrid">Hybrid</option>
                        </select>
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.fuel_type || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">NCB %</Label>
                      {editMode ? (
                        <Input
                          value={formData.ncb_percentage || ''}
                          onChange={(e) => handleInputChange('ncb_percentage', e.target.value)}
                          placeholder="e.g., 20%, 50%"
                          className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm"
                        />
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.ncb_percentage || '0%'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#3A4557] text-xs">Plan Type</Label>
                      {editMode ? (
                        <select
                          value={formData.plan_name || ''}
                          onChange={(e) => handleInputChange('plan_name', e.target.value)}
                          className="mt-1 w-full h-9 px-3 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm"
                        >
                          <option value="">Select</option>
                          <option value="Comprehensive">Comprehensive</option>
                          <option value="Third Party">Third Party Only</option>
                          <option value="Own Damage">Own Damage Only</option>
                        </select>
                      ) : (
                        <p className="text-[#1B2A4A] text-sm mt-1">{formData.plan_name || '-'}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Vehicle Add-ons */}
                  {formData.addons?.length > 0 && (
                    <div className="mt-4">
                      <Label className="text-[#3A4557] text-xs mb-2">Add-ons</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {formData.addons.map((addon, idx) => (
                          <span key={idx} className="px-2 py-1 bg-[#D4A017]/15 text-[#A87608] rounded text-xs">
                            {addon}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Client Referred By */}
              <div className="p-4 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-lg">
                <Label className="text-[#2E3B57] flex items-center gap-2 mb-3">
                  <Users className="h-4 w-4" />
                  Client Referred By
                </Label>
                <div className="flex flex-wrap gap-3">
                  {['Direct', 'Beshak'].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => handleInputChange('referred_by', option)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        formData.referred_by === option
                          ? 'bg-[#D4A017] text-[#0D1A3A]'
                          : 'bg-[#E2E9F4] text-[#2E3B57] hover:bg-[#E2E9F4]'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              {/* Platform Selection */}
              <div>
                <Label className="text-[#3A4557] text-sm">Platform / Source</Label>
                <select
                  value={formData.platform}
                  onChange={(e) => handleInputChange('platform', e.target.value)}
                  className="mt-1 w-full h-10 px-3 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A]"
                  data-testid="platform-select"
                >
                  {platforms.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                  {platforms.length === 0 && <option value="Direct">Direct</option>}
                </select>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {!showAddPlatform && !showRenamePlatform && (
                    <button type="button" onClick={() => setShowAddPlatform(true)}
                      className="flex items-center gap-1 text-xs text-[#A87608] hover:text-[#A87608]"
                      data-testid="add-platform-btn">
                      <Plus className="h-3 w-3" /> Add Platform
                    </button>
                  )}
                  {showAddPlatform && (
                    <div className="flex gap-2 w-full">
                      <input type="text" value={newPlatformName} onChange={(e) => setNewPlatformName(e.target.value)}
                        placeholder="Platform name" onKeyDown={(e) => e.key === 'Enter' && handleAddPlatform()}
                        className="flex-1 h-8 px-2 bg-white/70 border border-[#C9D4E6] rounded text-[#1B2A4A] text-sm"
                        data-testid="new-platform-input" />
                      <button type="button" onClick={handleAddPlatform} disabled={addingPlatform}
                        className="h-8 px-3 bg-[#D4A017] hover:bg-[#D4A017] text-[#0D1A3A] font-medium text-xs rounded disabled:opacity-50">
                        {addingPlatform ? "..." : "Add"}
                      </button>
                      <button type="button" onClick={() => { setShowAddPlatform(false); setNewPlatformName(""); }}
                        className="h-8 px-2 text-[#3A4557] hover:text-[#1B2A4A]">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  {showRenamePlatform && (
                    <div className="flex gap-2 w-full">
                      <input type="text" value={renamePlatformName} onChange={(e) => setRenamePlatformName(e.target.value)}
                        placeholder="New platform name" onKeyDown={(e) => e.key === 'Enter' && handleRenamePlatform()}
                        className="flex-1 h-8 px-2 bg-white/70 border border-[#C9D4E6] rounded text-[#1B2A4A] text-sm"
                        data-testid="rename-platform-input" />
                      <button type="button" onClick={handleRenamePlatform}
                        className="h-8 px-3 bg-[#D4A017] hover:bg-[#D4A017] text-[#0D1A3A] font-medium text-xs rounded"
                        data-testid="rename-platform-save-btn">
                        Save
                      </button>
                      <button type="button" onClick={() => { setShowRenamePlatform(false); setRenamePlatformName(""); }}
                        className="h-8 px-2 text-[#3A4557] hover:text-[#1B2A4A]">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  {formData.platform && formData.platform !== "Direct" && !showAddPlatform && !showRenamePlatform && (
                    <div className="flex gap-3 ml-auto">
                      <button type="button" onClick={() => { setRenamePlatformName(formData.platform); setShowRenamePlatform(true); }}
                        className="flex items-center gap-1 text-xs text-[#2E3B57] hover:text-[#1B2A4A]"
                        data-testid="rename-platform-btn">
                        <Edit2 className="h-3 w-3" /> Edit
                      </button>
                      <button type="button" onClick={() => handleDeletePlatform(formData.platform)}
                        className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                        data-testid="delete-platform-btn">
                        <Trash2 className="h-3 w-3" /> Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSavePolicy}
                disabled={saving}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white font-bold py-6 text-lg"
                data-testid="save-policy-btn"
              >
                {saving ? (
                  <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Saving...</>
                ) : (
                  <><Save className="h-5 w-5 mr-2" />Save Policy</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Help Text */}
        {!extractedData && !extracting && (
          <div className="text-center py-8">
            <p className="text-[#3A4557] text-sm">
              Upload any insurance policy PDF. AI will automatically extract:<br/>
              Client name, Policy details, Premium, Dates, Members, Riders & more
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
